import type { LearnCollectionsCatalogueFilters } from '~/composables/Learn/Collections/useLearnCollectionsCatalogue';

import { useIndustryHubCards } from '~/composables/unified/useIndustryHubCards';

export const EXPERIENCE_FILTER_KEYS = ['industry', 'type', 'coins', 'length'] as const;
export type ExperienceFilterKey = typeof EXPERIENCE_FILTER_KEYS[number];

export interface ExperienceFilterGroup {
  key: ExperienceFilterKey;
  label: string;
  options: { value: string; label: string }[];
}

// Each option's URL value and the catalogue filter it stands for. Coin and length bands follow the
// spread of the catalogue's totals, and start at 1 because a card hides a total of 0; `type` splits partner experiences from Anyway's own.
const TYPE_FILTERS: Record<string, LearnCollectionsCatalogueFilters> = {
  partner: { hasPartner: true },
  anyway: { hasPartner: false },
};
const COIN_FILTERS: Record<string, LearnCollectionsCatalogueFilters> = {
  'under-200': { minCoins: 1, maxCoins: 199 },
  '200-499': { minCoins: 200, maxCoins: 499 },
  '500-plus': { minCoins: 500 },
};
// Consideration-area slugs are lowercase kebab-case.
const INDUSTRY_SLUG = /^[a-z0-9-]+$/;
const LENGTH_FILTERS: Record<string, LearnCollectionsCatalogueFilters> = {
  'under-10': { minDurationMinutes: 1, maxDurationMinutes: 9 },
  '10-30': { minDurationMinutes: 10, maxDurationMinutes: 30 },
  'over-30': { minDurationMinutes: 31 },
};

/**
 * The Experiences category's filters, held in the URL (`industry`, `type`, `coins`, `length`) so a
 * filtered view can be shared and is restored when the student comes back from a collection.
 */
export const useExperienceFilters = async () => {
  const { t } = useI18n();
  const route = useRoute();
  const router = useRouter();
  const { hubs } = await useIndustryHubCards();

  const allGroups = computed<ExperienceFilterGroup[]>(() => [
    {
      key: 'industry',
      label: t('unified.learnIa.filters.industry.label'),
      options: hubs.value.map(hub => ({ value: hub.slug, label: hub.title })),
    },
    {
      key: 'type',
      label: t('unified.learnIa.filters.type.label'),
      options: Object.keys(TYPE_FILTERS).map(value => ({ value, label: t(`unified.learnIa.filters.type.${value}`) })),
    },
    {
      key: 'coins',
      label: t('unified.learnIa.filters.coins.label'),
      options: Object.keys(COIN_FILTERS).map(value => ({ value, label: t(`unified.learnIa.filters.coins.${value}`) })),
    },
    {
      key: 'length',
      label: t('unified.learnIa.filters.length.label'),
      options: Object.keys(LENGTH_FILTERS).map(value => ({ value, label: t(`unified.learnIa.filters.length.${value}`) })),
    },
  ]);
  // The hub list loads lazily and can come back empty, so the Industry pill waits for options.
  const groups = computed(() => allGroups.value.filter(group => group.options.length > 0));

  // Only a value one of the groups offers counts; anything else in the URL is ignored. Until the
  // hub list has arrived (or if it never does) any slug-shaped industry is trusted, so a shared or
  // restored link filters from the first request instead of flashing the unfiltered list.
  const isOffered = (group: ExperienceFilterGroup, value: string) =>
    group.options.some(option => option.value === value)
    || (group.key === 'industry' && group.options.length === 0 && INDUSTRY_SLUG.test(value));

  const selected = computed<Partial<Record<ExperienceFilterKey, string>>>(() =>
    Object.fromEntries(allGroups.value.flatMap((group) => {
      const value = route.query[group.key];
      return typeof value === 'string' && isOffered(group, value) ? [[group.key, value]] : [];
    })));

  const hasFilters = computed(() => Object.keys(selected.value).length > 0);

  const catalogueFilters = computed<LearnCollectionsCatalogueFilters>(() => ({
    considerationAreaSlug: selected.value.industry,
    ...(selected.value.type ? TYPE_FILTERS[selected.value.type] : {}),
    ...(selected.value.coins ? COIN_FILTERS[selected.value.coins] : {}),
    ...(selected.value.length ? LENGTH_FILTERS[selected.value.length] : {}),
  }));

  // `replace`, as the search does: picking filters is a state of this page, not new history.
  const setFilter = (key: ExperienceFilterKey, value: string | undefined) =>
    router.replace({ query: { ...route.query, [key]: value || undefined } });

  const clearFilters = () =>
    router.replace({ query: Object.fromEntries(Object.entries(route.query).filter(([key]) => !(EXPERIENCE_FILTER_KEYS as readonly string[]).includes(key))) });

  return { groups, selected, hasFilters, catalogueFilters, setFilter, clearFilters };
};
